import { Button, Menu } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconChevronDown,
  IconCopy,
  IconHeart,
  IconPlus,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import AutoSaveModal from "src/generator/_Modals/AutoSaveModal/AutoSaveModal";
import FavouritesModal from "src/generator/_Modals/FavouritesModal/FavouritesModal";
import { useOpenLogbookEntry } from "src/generator/hooks/useOpenLogbookEntry";
import { useCheckPerm, useGetApi } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  resetEntryLock,
  setNoSaves,
  setReleaseEntryLock,
} from "src/redux/slices/entrySlice";
import {
  setForceTurnOffInlineSave,
  setInitiallyOpen,
  setIsInitialInsert,
  setNewEntryData,
} from "src/redux/slices/generatorSlice";
import { resolveEntryId } from "src/utils/logbookUtils/resolveEntryId";

import useResetGuernsey from "../../../components/shared/GuernseyMulti/useResetGuernsey";
import { useClearCursorPositions } from "../../../hooks/useSavedCursorPosition";
import { useClearScrollState } from "../../../hooks/useSavedHorizontalScroll";
import {
  setGuernseyData,
  setNMEAModalData,
} from "../../../redux/slices/generatorFeaturesSlice";
import {
  getNewEntryParams,
  type NewEntryMode,
  resetCopiedEntryDates,
} from "./NewEntryButton.utils";

interface Props {
  hamburger?: boolean;
  alternativeText?: string;
}

const NewEntryButton = ({ hamburger, alternativeText }: Props) => {
  const lbid = useSelector(state => state.generatorSlice.entryDataToSave.lbid);
  const {
    entryType,
    copyEntry: canCopy,
    favourites: canUseFavourites,
  } = useSelector(state => state.generatorSlice.lbentry);
  const currentEntryId = useSelector(
    state => state.generatorSlice.latestEntry.id,
  );
  const isV2 = useSelector(
    state => state.generatorSlice.lbentry.generator === "v2",
  );
  const clearScrollsState = useClearScrollState();
  const clearCursorPositions = useClearCursorPositions();
  const { draft } = useSelector(state => state.generatorSlice);
  const openEntry = useOpenLogbookEntry();
  const [opened, { open, close }] = useDisclosure(false);
  const [favouritesOpened, { open: openFavourites, close: closeFavourites }] =
    useDisclosure(false);
  const modeRef = useRef<NewEntryMode>({ type: "blank" });

  const dispatch = useDispatch();
  const [, setSearchParams] = useSearchParams();
  const checkPerm = useCheckPerm();
  const resetGuernsey = useResetGuernsey();

  const { refetch: fetchNewEntry, isLoading } = useGetApi({
    request: "new-entry",
    body: { params: { lbid } },
    fetchOnInit: false,
  });

  const fetchEntryForMode = useCallback(
    (mode: NewEntryMode) =>
      fetchNewEntry({ params: { lbid, ...getNewEntryParams(mode) } }),
    [fetchNewEntry, lbid],
  );

  const handleNewEntry = useCallback(
    (data: any) => {
      const fetchedEntry = data?.data?.lbentry;
      const nextEntry =
        fetchedEntry && modeRef.current.type !== "blank"
          ? resetCopiedEntryDates(fetchedEntry)
          : fetchedEntry;
      setSearchParams({ new: "true" }, { replace: true });
      clearScrollsState();
      clearCursorPositions();
      if (nextEntry)
        dispatch(
          setNewEntryData({
            lbentry: nextEntry,
          }),
        );
      resetGuernsey();
      dispatch(setReleaseEntryLock(Math.random()));
      dispatch(setNMEAModalData(nextEntry?.NMEA ? { opened: true } : null));
      dispatch(setIsInitialInsert(true));
      dispatch(setForceTurnOffInlineSave(Math.random()));
      dispatch(setGuernseyData(null));
    },
    [
      dispatch,
      setSearchParams,
      clearScrollsState,
      clearCursorPositions,
      resetGuernsey,
    ],
  );

  const parseNew = async (data: any) => {
    if (!draft) {
      handleNewEntry(data);
      if (!isV2) {
        dispatch(resetEntryLock());
      }
    } else {
      open();
    }
  };

  const startNewEntry = async (mode: NewEntryMode) => {
    modeRef.current = mode;
    dispatch(setNoSaves(0));

    const newEntryData = await fetchEntryForMode(mode);

    void parseNew(newEntryData);
  };

  const refetchAndHandle = async () => {
    const newEntryData = await fetchEntryForMode(modeRef.current);
    handleNewEntry(newEntryData);
  };

  // Same format as the entry select labels.
  const draftDate = draft?.datecreated
    ? dayjs(draft.datecreated).format("D MMM YYYY")
    : "";

  const openDraftEntry = () => {
    const draftEntryId = resolveEntryId(draft);
    if (!draftEntryId || draftEntryId === String(currentEntryId)) return;
    dispatch(setInitiallyOpen(false));
    dispatch(setReleaseEntryLock(Math.random()));
    openEntry(draftEntryId);
  };

  if (!checkPerm("createentries") || entryType === "none") {
    return <></>;
  }

  const hasSavedEntry = !!currentEntryId && currentEntryId !== "new";
  const onNewEntry = () => void startNewEntry({ type: "blank" });
  const onCopyEntry = () =>
    void startNewEntry({ type: "copy", lbeid: currentEntryId });
  const onFavourite = (favid: string) =>
    void startNewEntry({ type: "favourite", favid });

  const extraItems = (
    <>
      {canCopy && (
        <Menu.Item
          disabled={!hasSavedEntry}
          onClick={onCopyEntry}
          leftSection={<IconCopy size={20} />}>
          Copy Previous Entry
        </Menu.Item>
      )}
      {canUseFavourites && (
        <Menu.Item
          onClick={openFavourites}
          leftSection={<IconHeart size={20} />}>
          From Favourite
        </Menu.Item>
      )}
    </>
  );

  const newEntryItem = (
    <Menu.Item onClick={onNewEntry} leftSection={<IconPlus size={20} />}>
      New Entry
    </Menu.Item>
  );

  const renderTrigger = () => {
    if (hamburger) {
      return (
        <>
          {newEntryItem}
          {extraItems}
        </>
      );
    }

    if (!canCopy && !canUseFavourites) {
      return (
        <Button
          data-cy="new-entry-button"
          variant="outline"
          leftSection={!alternativeText && <IconPlus size={"1rem"} />}
          ml={15}
          loading={isLoading}
          onClick={onNewEntry}>
          {alternativeText ?? "New Entry"}
        </Button>
      );
    }

    return (
      <Menu shadow="md" width={220} position="bottom-end">
        <Menu.Target>
          <Button
            data-cy="new-entry-button"
            variant="outline"
            leftSection={!alternativeText && <IconPlus size={"1rem"} />}
            rightSection={<IconChevronDown size={"1rem"} />}
            ml={15}
            loading={isLoading}>
            {alternativeText ?? "New Entry"}
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {newEntryItem}
          {extraItems}
        </Menu.Dropdown>
      </Menu>
    );
  };

  return (
    <>
      <AutoSaveModal
        opened={opened}
        close={close}
        fetchNewEntry={refetchAndHandle}
        openDraftEntry={openDraftEntry}
        dateCreated={draftDate}
        fullName={draft?.full_name}
      />
      {canUseFavourites && (
        <FavouritesModal
          opened={favouritesOpened}
          close={closeFavourites}
          onSelect={onFavourite}
        />
      )}
      {/*<ExistingEntryModal*/}
      {/*  opened={openExistingEntryModal}*/}
      {/*  close={() => setOpenExistingEntryModal(false)}*/}
      {/*  handleNewEntry={() => parseNew(data)}*/}
      {/*  date={entryModalDate}*/}
      {/*/>*/}
      {renderTrigger()}
    </>
  );
};

export default NewEntryButton;
